import * as Sentry from '@sentry/remix';
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import { startTransition, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { posthogManager } from '~/lib/services/posthog-manager';
import '~/lib/i18n';
import { MAINTENANCE_MODE } from '~/utils/constants';

// if (process.env.NODE_ENV === 'production') {
//   // Display warning message before disabling console logs
//   console.log('%c⚠️ Warning! ⚠️', 'color: red; font-size: 24px; font-weight: bold; text-shadow: 1px 1px 1px black;');
//   console.log(
//     '%cThis is a browser feature intended for developers. Do not paste any code here that you do not understand. It could compromise your account security.',
//     'color: #e67e22; font-size: 16px; font-weight: bold;',
//   );

//   console.log = () => undefined;
// }

function PosthogInit() {
  // Debug logging for environment variables
  console.log('[PosthogInit] === Environment Variables Debug ===');
  console.log('[PosthogInit] window.ENV:', (window as any).ENV);
  console.log('[PosthogInit] POSTHOG_KEY:', (window as any).ENV?.POSTHOG_KEY ? 'Present' : 'Missing');
  console.log('[PosthogInit] POSTHOG_HOST:', (window as any).ENV?.POSTHOG_HOST ? 'Present' : 'Missing');
  console.log('[PosthogInit] SENTRY_DSN:', (window as any).ENV?.SENTRY_DSN ? 'Present' : 'Missing');
  console.log('[PosthogInit] EARLY_ACCESS_URL:', (window as any).ENV?.EARLY_ACCESS_URL ? 'Present' : 'Missing');

  useEffect(() => {
    const initializePostHog = async () => {
      console.log('[PosthogInit] Initializing PostHog...');

      if (!(window as any).ENV?.POSTHOG_KEY) {
        console.error('[PosthogInit] Missing POSTHOG_KEY - PostHog will not be initialized');
        return;
      }

      if (!(window as any).ENV?.POSTHOG_HOST) {
        console.error('[PosthogInit] Missing POSTHOG_HOST - PostHog will not be initialized');
        return;
      }

      try {
        // Use PostHog manager with automatic fallback
        const success = await posthogManager.initialize((window as any).ENV.POSTHOG_KEY);

        if (success) {
          const status = posthogManager.getConnectionStatus();
          console.log(
            '[PosthogInit] PostHog initialized successfully',
            status.useProxy ? 'with reverse proxy' : 'with direct connection',
          );

          if (status.fallbackAttempted) {
            console.log('[PosthogInit] Note: Fallback to direct connection was used');
          }
        } else {
          console.error('[PosthogInit] Failed to initialize PostHog with both proxy and direct connection');
        }
      } catch (error) {
        console.error('[PosthogInit] Failed to initialize PostHog:', error);
      }
    };

    initializePostHog();
  }, []);

  return null;
}

// check maintenance mode
async function checkHealth() {
  console.log('[Health Check] === Starting Health Check ===');

  // Skip health check if already on maintenance page
  if (window.location.pathname === '/maintenance') {
    console.log('[Health Check] Already on maintenance page, skipping check');
    return false;
  }

  try {
    const isUnderMaintenance = MAINTENANCE_MODE;

    console.log('[Health Check] Response data:', isUnderMaintenance);

    if (isUnderMaintenance) {
      console.log('[Health Check] Maintenance mode is active, redirecting...');
      window.location.href = '/maintenance';
      throw new Error('Application in maintenance mode');
    }

    console.log('[Health Check] Health check passed');
    return true;
  } catch (error) {
    console.error('[Health Check] Health check failed:', error);
    window.location.href = '/maintenance';
    return false;
  }
}

// Execute health check before hydrating the app
checkHealth().then((isHealthy) => {
  if (!isHealthy) {
    console.log('Application is in maintenance mode');
  }
});

console.log('[Sentry] === Initializing Sentry ===');

const dsn = (window as any).ENV?.SENTRY_DSN;
const posthogKey = (window as any).ENV?.POSTHOG_KEY;
const posthogHost = (window as any).ENV?.POSTHOG_HOST;

console.log('[Sentry] DSN:', dsn ? 'Present' : 'Missing');
console.log('[Sentry] POSTHOG_KEY:', posthogKey ? 'Present' : 'Missing');
console.log('[Sentry] POSTHOG_HOST:', posthogHost ? 'Present' : 'Missing');

if (dsn) {
  console.log('[Sentry] Configuring Sentry with DSN');
  Sentry.init({
    dsn,
    tracesSampleRate: 1,

    // handle blocked requests
    beforeSend(event) {
      if (event.exception) {
        const error = event.exception.values?.[0];

        if (error?.type === 'NetworkError' && error?.value?.includes('ERR_BLOCKED_BY_CLIENT')) {
          console.log('Sentry request blocked by client - this is expected with ad blockers');
          return null;
        }
      }

      return event;
    },
    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
    ],
  });
}

startTransition(() => {
  hydrateRoot(
    document.getElementById('root')!,
    <>
      <PosthogInit />
      <RemixBrowser />
    </>,
  );
});
